import React, { useEffect, useCallback } from 'react';
import Script from 'next/script';
import { EventError, EventResult, CustomWindow, InitializedUploadWidget, MaybeEventError } from './types';
declare const window: CustomWindow;
interface CloudinaryUploadWidgetParams {
  /**
   * User-supplied load handler that receives the initialized and ready-to-open widget.
   */
  onLoad: (widget: InitializedUploadWidget) => void;

  /**
   * User-supplied error handler that receives upload widget event errors.
   */
  onError?: (error: EventError) => void;

  /**
   * User-supplied result handler that receives upload widget event results.
   */
  onResult?: (result: EventResult) => void;
}

/**
 * Component that initializes the Cloudinary Upload Widget.
 */
const CloudinaryUploadWidget = ({
  onLoad,
  onError,
  onResult
}: CloudinaryUploadWidgetParams) => {
  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
  const options: CloudinaryUploadWidgetParams = React.useMemo(() => ({
    cloudName,
    uploadPreset: 'client-selfie-uploads',
    onLoad,
    // Add the missing onLoad property to fix the error
    transformation: 'f_png,fl_strip',
    sources: ['local', 'camera'],
    multiple: false,
    showPoweredBy: false,
    // Hide Cloudinary branding
    autoUpload: false,
    // Don't auto-upload, wait for user to click "Done"
    // Force manual upload control
    cropping: true,
    // Enable cropping to force manual control
    styles: {
      palette: {
        window: '#FFFFFF',
        windowBorder: '#90A0B3',
        tabIcon: '#0078FF',
        menuIcons: '#5A616A',
        textDark: '#000000',
        textLight: '#FFFFFF',
        link: '#0078FF',
        action: '#FF620C',
        inactiveTabIcon: '#0E2F5A',
        error: '#F44235',
        inProgress: '#0078FF',
        complete: '#20B832',
        sourceBg: '#E4EBF1'
      },
      fonts: {
        default: null,
        "'Fira Sans', sans-serif": {
          url: 'https://fonts.googleapis.com/css?family=Fira+Sans',
          active: true
        }
      }
    }
  }), [cloudName, onLoad]);
  const resultCallback = useCallback((error: MaybeEventError, result: EventResult) => {
    if (error && onError) {
      onError(error);
    } else if (onResult) {
      onResult(result);
    }
  }, [onError, onResult]);
  useEffect(() => {
    const initializeWidget = () => {
      if (window.cloudinary) {
        const uploadWidget: InitializedUploadWidget = window.cloudinary.createUploadWidget(options, resultCallback);
        onLoad(uploadWidget);
        return uploadWidget;
      }
      return null;
    };
    let uploadWidget = initializeWidget();
    let timerId: ReturnType<typeof setTimeout> | null = null;
    if (!uploadWidget && typeof window !== 'undefined') {
      const checkCloudinary = () => {
        if (typeof window === 'undefined') return;
        if (window.cloudinary) {
          uploadWidget = initializeWidget();
        } else {
          timerId = setTimeout(checkCloudinary, 100);
        }
      };
      checkCloudinary();
    }
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [options, onLoad, resultCallback]);
  return <Script src="https://upload-widget.cloudinary.com/global/all.js" />;
};
export default CloudinaryUploadWidget;